import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Shield, Search, CheckCircle2,
  ChevronRight, FileText, Grid3X3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { requirements, areas } from '../data/mockData';

const RequirementsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('id') || '');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedReq, setSelectedReq] = useState(
    searchParams.get('id') ? requirements.find(r => r.id === searchParams.get('id')) : null
  );
  const [viewMode, setViewMode] = useState('list');

  const categories = [...new Set(requirements.map(r => r.category))];

  const filteredRequirements = requirements.filter(req => {
    const matchesSearch = req.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         req.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || req.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const avgCoverage = Math.round(requirements.reduce((sum, r) => sum + r.coverage, 0) / requirements.length);

  const RequirementRow = ({ req }) => (
    <TableRow 
      className={`cursor-pointer hover:bg-slate-50 ${selectedReq?.id === req.id ? 'bg-slate-100' : ''}`}
      onClick={() => setSelectedReq(req)}
      data-testid={`req-row-${req.id}`}
    >
      <TableCell className="font-mono text-xs text-[#fd4816]">{req.id}</TableCell>
      <TableCell>
        <div className="max-w-sm">
          <p className="text-sm text-slate-800 font-medium">{req.title}</p>
          <p className="text-xs text-slate-500">{req.category}</p>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Progress value={req.coverage} className="h-2 w-16 bg-slate-200" />
          <span className={`text-xs font-medium ${
            req.coverage >= 90 ? 'text-emerald-600' :
            req.coverage >= 80 ? 'text-amber-600' : 'text-red-600'
          }`}>{req.coverage}%</span>
        </div>
      </TableCell>
      <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{req.scheduleMRef}</TableCell>
      <TableCell>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </TableCell>
    </TableRow>
  );

  const RequirementDetail = ({ req }) => (
    <div className="space-y-4">
      <div>
        <Badge className="bg-[#fd4816]/10 text-[#fd4816] border border-[#fd4816]/30 text-xs font-mono mb-2">
          {req.id}
        </Badge>
        <h3 className="text-lg font-medium text-slate-900">{req.title}</h3>
        <p className="text-sm text-slate-600 mt-1">{req.category}</p>
      </div>

      <div className="bg-white rounded-md p-4 border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Coverage</p>
          <span className={`text-lg font-bold ${
            req.coverage >= 90 ? 'text-emerald-600' :
            req.coverage >= 80 ? 'text-amber-600' : 'text-red-600'
          }`}>{req.coverage}%</span>
        </div>
        <Progress value={req.coverage} className="h-3 bg-slate-200" />
      </div>

      <Separator className="bg-slate-200" />

      <div className="grid grid-cols-1 gap-3">
        <div className="bg-white rounded-md p-3 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Schedule-M Reference</p>
          <p className="text-sm text-slate-800">{req.scheduleMRef}</p>
        </div>
        {req.whoRef && (
          <div className="bg-white rounded-md p-3 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">WHO Reference</p>
            <p className="text-sm text-slate-800">{req.whoRef}</p>
          </div>
        )}
      </div>

      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Evidence Expected</p>
        <div className="space-y-1.5">
          {req.evidenceExpected?.map((evidence, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-white rounded-md px-3 py-2 border border-slate-200">
              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
              <span className="text-sm text-slate-700">{evidence}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Controls Mapped</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white rounded-md p-3 border border-slate-200 text-center">
            <p className="text-lg font-bold text-slate-800">3</p>
            <p className="text-xs text-slate-500">Checklists</p>
          </div>
          <div className="bg-white rounded-md p-3 border border-slate-200 text-center">
            <p className="text-lg font-bold text-slate-800">5</p>
            <p className="text-xs text-slate-500">SOPs</p>
          </div>
          <div className="bg-white rounded-md p-3 border border-slate-200 text-center">
            <p className="text-lg font-bold text-slate-800">2</p>
            <p className="text-xs text-slate-500">Training</p>
          </div>
          <div className="bg-white rounded-md p-3 border border-slate-200 text-center">
            <p className="text-lg font-bold text-slate-800">1</p>
            <p className="text-xs text-slate-500">System Control</p>
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <div className="flex gap-2 pt-2">
          <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white flex-1" data-testid="map-control">
            <FileText className="h-4 w-4 mr-2" />
            Map New Control
          </Button>
        </div>
      )}
    </div>
  );

  const HeatmapCell = ({ coverage }) => {
    const bgColor = coverage >= 90 ? 'bg-emerald-500' : 
                   coverage >= 80 ? 'bg-amber-500' : 
                   coverage >= 70 ? 'bg-amber-600' : 'bg-red-500';
    return (
      <div className={`${bgColor} w-full h-8 rounded flex items-center justify-center text-xs font-medium text-white`}>
        {coverage}%
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="requirements-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Requirements Library
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Schedule-M and WHO-GMP requirement mapping with coverage tracking
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
            Avg Coverage: {avgCoverage}%
          </Badge>
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
            {requirements.length} Requirements
          </Badge>
          {/* <ReportExportButton 
            title="Requirements Library Report"
            subtitle="Schedule-M and WHO-GMP requirement mapping with coverage tracking"
          /> */}
        </div>
      </div>

      <ReportSection id="requirements-metrics" title="Requirements Metrics" description="Coverage statistics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.1" req="SM-PQS-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Schedule-M</p>
              <p className="text-2xl font-bold text-slate-900">{requirements.filter(r => r.id.startsWith('SM-')).length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">WHO-GMP</p>
              <p className="text-2xl font-bold text-blue-600">{requirements.filter(r => r.id.startsWith('WHO-')).length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">High Coverage (90%+)</p>
              <p className="text-2xl font-bold text-emerald-600">{requirements.filter(r => r.coverage >= 90).length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Gaps (&lt;80%)</p>
              <p className="text-2xl font-bold text-red-600">{requirements.filter(r => r.coverage < 80).length}</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <Tabs defaultValue="list" onValueChange={setViewMode}>
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="list" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            List View
          </TabsTrigger>
          <TabsTrigger value="heatmap" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Grid3X3 className="h-4 w-4 mr-2" />
            Coverage Heatmap
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <ReportSection id="requirements-filters" title="Filters" description="Search and filter requirements">
                <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search requirements..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 bg-white border-slate-200 text-slate-700"
                          data-testid="req-search"
                        />
                      </div>
                      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-[200px] bg-white border-slate-200 text-slate-700">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          <SelectItem value="all" className="text-slate-700">All Categories</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat} className="text-slate-700">{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </ReportSection>

              <ReportSection id="requirements-list" title="Requirements List" description="All requirements">
                <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                  <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      <TableHead className="text-slate-600">ID</TableHead>
                      <TableHead className="text-slate-600">Requirement</TableHead>
                      <TableHead className="text-slate-600">Coverage</TableHead>
                      <TableHead className="text-slate-600">Reference</TableHead>
                      <TableHead className="text-slate-600 w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequirements.map(req => (
                      <RequirementRow key={req.id} req={req} />
                    ))}
                  </TableBody>
                </Table>
              </Card>
              </ReportSection>
            </div>

            <div className="lg:col-span-1">
              <Card className={`bg-white border border-slate-200 shadow-sm sticky top-20 relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
                <TraceLabel prd="PRD §6.1" req="SM-DOC-001" />
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-800">Requirement Detail</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[calc(100vh-320px)]">
                    {selectedReq ? (
                      <RequirementDetail req={selectedReq} />
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Select a requirement to view details</p>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="heatmap" className="mt-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.1" req="SM-PQS-001" />
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-800">Coverage Heatmap: Requirements vs Plant Areas</CardTitle>
              <CardDescription className="text-slate-600">Click on any cell to view gap details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left text-xs text-slate-600 p-2 min-w-[180px]">Requirement</th>
                      {areas.slice(0, 4).map(area => (
                        <th key={area.id} className="text-center text-xs text-slate-600 p-2 min-w-[100px]">
                          {area.name.split(' – ')[0]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {requirements.slice(0, 10).map(req => (
                      <tr key={req.id}>
                        <td className="text-xs text-slate-700 p-2 border-t border-slate-200">
                          <span className="font-mono text-[#fd4816]">{req.id}</span>
                          <br />
                          <span className="text-slate-500 text-[10px]">{req.title.substring(0, 30)}...</span>
                        </td>
                        {areas.slice(0, 4).map((area, idx) => (
                          <td key={area.id} className="p-1 border-t border-slate-200">
                            <HeatmapCell coverage={Math.max(60, req.coverage - (idx * 5) + Math.floor(Math.random() * 10))} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-emerald-500" />
                  <span className="text-xs text-slate-600">90%+ (Compliant)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-amber-500" />
                  <span className="text-xs text-slate-600">80-89% (Needs Attention)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500" />
                  <span className="text-xs text-slate-600">&lt;80% (Gap)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RequirementsPage;
